import { ReactElement } from "react";
import Button from "@aleaplay/web-components/ui/Button";
import Modal from "@aleaplay/web-components/ui/Modal";

import { useTranslation } from "@/v2/hooks/useTranslation";

import { FreeSpinsRetrievalError } from "./components/FreeSpinsRetrievalError";
import { FreeSpinsRetrievalFailures } from "./components/FreeSpinsRetrievalFailures";
import { FreeSpinsRetrievalRunning } from "./components/FreeSpinsRetrievalRunning";
import { FreeSpinsRetrievalSuccess } from "./components/FreeSpinsRetrievalSuccess";
import { useFreeSpinsRetrievalStore } from "./store/useFreeSpinsRetrievalStore";
import { TARGET_TYPE_LABEL } from "./utils/constants";
import { isSameTarget } from "./utils/isSameTarget";

import "./freeSpinsRetrievalModal.scss";

/** The FS retrieval modal: running, success, failed games or error. */
export const FreeSpinsRetrievalModal = (): ReactElement => {
  const { t } = useTranslation();
  const retrieval = useFreeSpinsRetrievalStore(
    (state) => state.retrievals.find(({ target }) => isSameTarget(target, state.shown)) ?? null,
  );
  const close = useFreeSpinsRetrievalStore((state) => state.close);

  const outcome = retrieval?.outcome ?? null;
  const result = outcome?.status === "done" ? outcome.result : null;
  const errorMessage = outcome?.status === "error" ? outcome.errorMessage : null;
  const hasFailures = Boolean(result?.failures.length);

  const isRunning = retrieval !== null && outcome === null;
  const isError = outcome?.status === "error";
  const isSuccess = result !== null && !hasFailures;
  const hasFailedGames = result !== null && hasFailures;

  // Names the downloaded file: the integrator or skin, and the game if only one.
  const { name = "", gameName } = retrieval?.target ?? {};
  const fileName = gameName ? `${name} ${gameName}` : name;

  return (
    <Modal
      title={t("free-spin.fs-retrieval-modal.title")}
      domNode={document.querySelector("#root")}
      visible={retrieval !== null}
      onClose={close}
      actions={[
        <Button
          key="close"
          modifier="primary"
          ripple
          onClick={close}
          data-testid="fs-retrieval-modal-close-button"
        >
          {t("label.close")}
        </Button>,
      ]}
    >
      <output className="fs-retrieval" aria-live="polite">
        {retrieval && (
          <p className="fs-retrieval__subtitle" data-testid="fs-retrieval-target">
            <span className="fs-retrieval__target-type">
              {t(TARGET_TYPE_LABEL[retrieval.target.type])}:
            </span>{" "}
            {retrieval.target.name}
            {retrieval.target.gameName && (
              <>
                {" · "}
                <span className="fs-retrieval__target-type">{t("label.game")}:</span>{" "}
                {retrieval.target.gameName}
              </>
            )}
          </p>
        )}
        {isRunning && <FreeSpinsRetrievalRunning />}
        {isError && <FreeSpinsRetrievalError message={errorMessage} />}
        {isSuccess && <FreeSpinsRetrievalSuccess result={result} />}
        {hasFailedGames && (
          <FreeSpinsRetrievalFailures
            result={result}
            targetName={fileName}
            showGamesTable={!gameName}
          />
        )}
      </output>
    </Modal>
  );
};
